// MessageAttachmentIndicator — the sources riding on a chat message, pure.
//
// Promoted from sketches/message-attachments/MessageAttachmentIndicator.tsx
// (dogfood F34 → F34i, findings-2026-09-22.md): replaces the bare
// `ResourceMentionChip` row ChatMessage used to render for a sent message's
// @-referenced resources. That chip painted a 1px border (project rule
// prefers tone/elevation over painted edges) and gave no visual distinction
// between "the author attached this" and a future "the agent drew on this
// while answering" — this component draws that distinction as two ROLES on
// one atom, not two components.
//
// `attached` — the author sent this WITH the turn (today's, and currently
// the ONLY, live case: `AuthorEntry.mentions`). Gets the design system's
// `--color-info`/`--color-info-bg` (calm, factual blue, per F38's palette
// check) — a small, deliberate signal that this is the author's own
// material, distinct from the ember accent reserved for the agent's voice.
// `referenced` — the agent drew on it while composing an answer, without the
// author attaching anything new turn-side. No live data source produces this
// yet (no BC event tags an agent reply with the resources it read); the role
// stays a real, exercised branch (see Storybook) so wiring it up later is a
// data-plumbing change, not a redesign.
//
// The prop is named `attachmentRole`, never bare `role` — a JSX `role`
// attribute reads as the ARIA role to biome's a11y linter even on a custom
// component, so a same-named prop would either trip that lint or (worse)
// get silently "fixed" away.
//
// Density mirrors ChatMessage's own `full`/`compact` reading contract: `full`
// is roomier (more breathing room, a higher collapse threshold), `compact` is
// the dense ambient row (tighter gaps, collapses sooner) — the SAME atoms,
// not a shrunk clone. Pure/props-driven: `attachments` is the only content
// input; the one piece of state (`expanded`, the +N-more disclosure) is
// view-ephemeral UI state, same posture as ChatMessage's own clamp/activity-
// trail expanders. No `as` casts, no borders, tokens only.

export { MessageAttachmentIndicator };

import { Trans } from "@lingui/react/macro";
import { type ReactNode, useState } from "react";
import type {
    ComposerResourceKind,
    ComposerResourceRef,
} from "../../Composer/PowerComposer/types.js";
import { Icon, type IconName } from "../../Icon/Icon.js";
import { Text } from "../../Text/Text.js";
import { VisuallyHidden } from "../../VisuallyHidden/VisuallyHidden.js";
import type { ChatDensity } from "../presentation.js";
import "./MessageAttachmentIndicator.css";

type MessageAttachmentRole = "attached" | "referenced";
type MessageAttachmentDensity = ChatDensity;

type MessageAttachmentIndicatorProps = {
    // The real @-mention/attachment refs already on the message — never
    // fabricated metadata. Rendered as-is; an empty array renders nothing.
    readonly attachments: readonly ComposerResourceRef[];
    // "attached" (default) — the author sent this WITH the turn. "referenced"
    // — the agent used it while composing its answer; renders quieter, with
    // a different hidden attribution, never colour alone.
    readonly attachmentRole?: MessageAttachmentRole;
    readonly density?: MessageAttachmentDensity;
    // How many chips show before the rest fold under "+N more". Absent ⇒ a
    // sensible density default (3 at full, 2 at compact) — callers only
    // override this for a story/demonstration, not in real use.
    readonly collapseAfter?: number;
    // Opening a chip (it shows in the right panel). Absent ⇒ static chips.
    readonly onOpen?: (attachment: ComposerResourceRef) => void;
};

const kindGlyph = (kind: ComposerResourceKind): IconName => {
    switch (kind) {
        case "source":
            return "source";
        case "material":
            return "material";
        case "package":
            return "folder";
        case "file":
            return "file";
    }
};

const AttachmentChip = ({
    attachment,
    attachmentRole,
    onOpen,
}: {
    readonly attachment: ComposerResourceRef;
    readonly attachmentRole: MessageAttachmentRole;
    readonly onOpen: ((attachment: ComposerResourceRef) => void) | undefined;
}): ReactNode => {
    const body = <AttachmentChipBody attachment={attachment} />;
    return onOpen !== undefined ? (
        <button
            type="button"
            className="bilda-message-attachment-chip"
            data-attachment-role={attachmentRole}
            data-status={attachment.status}
            title={attachment.title}
            onClick={() => onOpen(attachment)}
        >
            {body}
        </button>
    ) : (
        <span
            className="bilda-message-attachment-chip"
            data-attachment-role={attachmentRole}
            data-status={attachment.status}
            title={attachment.title}
        >
            {body}
        </span>
    );
};

const AttachmentChipBody = ({
    attachment,
}: {
    readonly attachment: ComposerResourceRef;
}): ReactNode => (
    <>
        <Icon
            name={kindGlyph(attachment.kind)}
            size="xs"
            className="bilda-message-attachment-chip__glyph"
        />
        <Text
            as="span"
            size="sm"
            weight="medium"
            className="bilda-message-attachment-chip__title"
        >
            {attachment.title}
        </Text>
        {/* F83: a sent chip whose upload is still landing — a quiet pulsing
            dot. Render-time decoration (attachment.status); a landed chip
            rests with nothing extra (D57). */}
        {attachment.status === "uploading" ? (
            <span
                className="bilda-message-attachment-chip__status"
                data-status={attachment.status}
                aria-hidden="true"
            />
        ) : null}
    </>
);

const MessageAttachmentIndicator = ({
    attachments,
    attachmentRole = "attached",
    density = "full",
    collapseAfter,
    onOpen,
}: MessageAttachmentIndicatorProps): ReactNode => {
    const [expanded, setExpanded] = useState(false);
    if (attachments.length === 0) return null;

    const threshold = collapseAfter ?? (density === "compact" ? 2 : 3);
    const overflow = attachments.length > threshold;
    const visible =
        overflow && !expanded ? attachments.slice(0, threshold) : attachments;
    const restCount = attachments.length - threshold;

    return (
        <div
            className={`bilda-message-attachment-row bilda-message-attachment-row--${attachmentRole} bilda-message-attachment-row--${density}`}
        >
            <VisuallyHidden>
                {attachmentRole === "attached" ? (
                    <Trans>Attached:</Trans>
                ) : (
                    <Trans>Referenced:</Trans>
                )}{" "}
            </VisuallyHidden>
            {visible.map((a) => (
                <AttachmentChip
                    key={a.id}
                    attachment={a}
                    attachmentRole={attachmentRole}
                    onOpen={onOpen}
                />
            ))}
            {overflow ? (
                <button
                    type="button"
                    className="bilda-message-attachment-more"
                    aria-expanded={expanded}
                    onClick={() => setExpanded((shown) => !shown)}
                >
                    <Text as="span" size="xs" tone="dim">
                        {expanded ? (
                            <Trans>Show less</Trans>
                        ) : (
                            <Trans>+{restCount} more</Trans>
                        )}
                    </Text>
                </button>
            ) : null}
        </div>
    );
};
